import { z } from 'zod';
import { GarageMediaKind, Id, MediaKind, MediaUploader, MediaVariant, MediaVisibility, ShortText, Timestamp } from './common.js';

export const MediaOutput = z.object({
  id: Id,
  interventionId: Id,
  kind: MediaKind,
  /**
   * Auteur (vague 4) : `customer` pour une photo jointe par le client (kind `customer`).
   * Le garage peut la retirer de la vue client (audité), jamais la modifier ni la publier comme sienne.
   */
  uploadedBy: MediaUploader,
  caption: z.string().nullable(),
  visibility: MediaVisibility,
  authorName: z.string().nullable(),
  createdAt: Timestamp,
  publishedAt: Timestamp.nullable(),
  width: z.int().nullable(),
  height: z.int().nullable(),
});
export type MediaOutput = z.infer<typeof MediaOutput>;

/** Champs texte du formulaire multipart d'envoi (le fichier est la partie `file`). */
export const MediaUploadFields = z.strictObject({
  kind: GarageMediaKind.default('report'),
  caption: ShortText.nullable().optional(),
});
export const MediaUpdateInput = z.strictObject({ caption: ShortText.nullable() });
export const PublishMediaInput = z.strictObject({ mediaIds: z.array(Id).min(1).max(200) });
export const MediaFileQuery = z.strictObject({ variant: MediaVariant.default('display') });

/** Photo telle que vue par le client : publiée uniquement, sans auteur ni données internes. */
export const PortalMediaOutput = z.object({
  id: Id,
  caption: z.string().nullable(),
  takenAt: Timestamp,
  publishedAt: Timestamp,
});
export type PortalMediaOutput = z.infer<typeof PortalMediaOutput>;

// ─── Photos jointes aux messages (vague 4) ───────────────────────────────────

/** Limites des photos jointes par le client (portail). */
export const PORTAL_MEDIA_LIMITS = {
  /** Taille maximale d'un fichier envoyé (octets). */
  maxBytes: 10 * 1024 * 1024,
  /** Photos jointes à une question au plus. */
  perMessage: 5,
  /** Envois par heure et par jeton. */
  uploadsPerHourPerToken: 20,
  /** Une photo envoyée mais jamais jointe est supprimée après ce délai (heures). */
  unlinkedTtlHours: 24,
} as const;

/** Photos du dossier jointes à une réponse du garage au plus. */
export const MESSAGE_MEDIA_MAX = 10;

/** Champs texte du formulaire multipart d'envoi par le client (le fichier est la partie `file`) : aucun. */
export const PortalMediaUploadFields = z.strictObject({});
/** Photo client reçue : identifiant à joindre ensuite à une question (`PortalQuestionInput.mediaIds`). */
export const PortalMediaUploadOutput = z.object({ id: Id });

/** Photo jointe à un message, côté garage (servie par `media.file`). */
export const MessageMediaOutput = z.object({
  id: Id,
  uploadedBy: MediaUploader,
  /** `internal` : photo client retirée de la vue client par le garage. */
  visibility: MediaVisibility,
  width: z.int().nullable(),
  height: z.int().nullable(),
});
export type MessageMediaOutput = z.infer<typeof MessageMediaOutput>;

/** Photo jointe à un message, côté client (servie par `portal.mediaFile`) : visible du client uniquement. */
export const PortalMessageMediaOutput = z.object({
  id: Id,
  uploadedBy: MediaUploader,
});
export type PortalMessageMediaOutput = z.infer<typeof PortalMessageMediaOutput>;
